import Vue from 'vue';
import store from '@/store';
import resetState from '@/helpers/resetState';
import { ABI } from './ABI';
import { formatData } from '@/constants/commonContent';
import { messages } from '@/constants/messages';
import { Contract } from 'web3-eth-contract';

const FEATURE_NAME = 'EscrowFeature';

let from;

async function contract(): Promise<Contract> {
  from = {from: store.getters.getWallet};
  const address = store.getters.getFeatureAddress(FEATURE_NAME);
  return new Promise(resolve => {
    let contract = new window.web3.eth.Contract(ABI, address);
    resolve(contract);
  });
}

const initialState = {
  tradeTransfersEscrow: {},
  tradeTransfersCounter: null,
  tokenTransfersEscrow: {},
  tokenTransfersCounter: null,
  isUsedEscrow: false
};

const state = {
  tradeTransfersEscrow: initialState.tradeTransfersEscrow,
  tradeTransfersCounter: initialState.tradeTransfersCounter,
  tokenTransfersEscrow: initialState.tokenTransfersEscrow,
  tokenTransfersCounter: initialState.tokenTransfersCounter,
  isUsedEscrow: initialState.isUsedEscrow
};

const getters = {
  getTradeTransfersEscrow: state =>
    Object.values(state.tradeTransfersEscrow).filter((trade: any) => trade.status === 0),
  getTokenTransfersEscrow: state =>
    Object.values(state.tokenTransfersEscrow).filter((transfer: any) => transfer.status === 0),
  getTokenTransferEscrow: state => id =>
    Object.values(state.tokenTransfersEscrow)
      .filter((transfer: any) => transfer.id === id && transfer.status === 0)
      .pop(),
  isUsedEscrow: state => state.isUsedEscrow
};

const mutations = {
  setTradeTransfersCounter: (state, item) => {
    state.tradeTransfersCounter = item;
  },
  setTradeTransferEscrow: (state, item) => {
    Vue.set(state.tradeTransfersEscrow, item.id, item);
  },
  setTokenTransfersCounter: (state, item) => {
    state.tradeTransfersCounter = item;
  },
  setTokenTransferEscrow: (state, item) => {
    Vue.set(state.tokenTransfersEscrow, item.id, item);
  },
  setIsUsedEscrow: (state, item) => {
    state.isUsedEscrow = item;
  },
  resetEscrowFeature: state => {
    resetState(state, initialState);
  }
};

const actions = {
  initEscrowFeature({ dispatch, getters, commit }) {
    dispatch('isUsedEscrowDapp');
    dispatch('getTradeTransfersDapp');
    dispatch('getTokenTransfersDapp');

    //Events
    contract().then(contract => {
      contract.events.EscrowTransferEvent().subscribe((err, res) => {
        let transactionHash = '0x';
        if (!err && transactionHash !== res.transactionHash) {
          const transferId = res.returnValues.id;
          if (!getters.getTokenTransferEscrow(transferId)) {
            commit('transferTokenPending', false);
          }
          commit('resetMailPending', 'reviewEscrow');
          dispatch('getFreezedOfDapp', getters.getWallet);
          dispatch('getBalancesDapp');
          dispatch('getTokenTransferDapp', transferId);
          commit('setEscrowTransferTokenPending', null);
        } else if (err) {
          commit('resetMailPending', 'reviewEscrow');
          commit('setEscrowTransferTokenPending', null);
        }
      });
    });

    contract().then(contract => {
      let transactionHash = '0x';
      contract.events.EscrowTradeEvent().subscribe((err, res) => {
        if (!err && transactionHash !== res.transactionHash) {
          const tradeId = res.returnValues.id;
          dispatch('getTradeTransferDapp', tradeId);
          commit('setEscrowTradePending', null);
        } else if (err) {
          commit('setEscrowTradePending', null);
        }
      });
    });
  },
  getTradeTransfersDapp({ dispatch }) {
    dispatch('getTradeTransfersCounterDapp').then(result => {
      for (let i = 0; i < result; i++) {
        dispatch('getTradeTransferDapp', i);
      }
    });
  },
  /**
   * Get trade transfer count
   * @return {Promise<number>}
   * @res[0] "trade transfer count" {number}
   */
  getTradeTransfersCounterDapp({ commit }) {
    return new Promise(resolve => {
      contract().then(contract => {
        contract.methods.getTradeTransferCounter()
          .call()
          .then(res => {
            commit('setTradeTransfersCounter', res);
            resolve(res);
          })
          .catch(console.error);
      });
    });
  },
  /**
   * Get trade transfer
   * @params id {number}
   * @res[0] "from" {number}
   * @res[1] "tradeId" {number}
   * @res[2] "orderId" {number}
   * @res[3] "mode" {number}
   * @res[4] "amount" {number}
   * @res[5] "value" {number}
   * @res[6] "createTime" {number}
   * @res[7] "resultTime" {number}
   * @res[8] "status" {number}
   * @res[9] "description" {string}
   */
  getTradeTransferDapp({ commit }, id) {
    contract().then(contract => {
      contract.methods.getTradeTransfer(id)
        .call()
        .then(res => {
          let data = {
            id,
            from: res[0],
            tradeId: parseInt(res[1]),
            orderId: parseInt(res[2]),
            mode: parseInt(res[3]),
            amount: parseInt(res[4]),
            value: parseInt(res[5]),
            createTime: parseInt(res[6]),
            resultTime: parseInt(res[7]),
            status: parseInt(res[8]),
            description: res[9]
          };
          commit('setTradeTransferEscrow', data);
        })
        .catch(console.error);
    });
  },
  getTokenTransfersDapp({ dispatch }) {
    dispatch('getTokenTransfersCounterDapp').then(result => {
      for (let i = 0; i < result; i++) {
        dispatch('getTokenTransferDapp', i);
      }
    });
  },
  /**
   * Get token transfer count
   * @return {Promise<number>}
   * @res[0] "token transfer count" {number}
   */
  getTokenTransfersCounterDapp({ commit }) {
    return new Promise(resolve => {
      contract().then(contract => {
        contract.methods.getTokenTransferCounter()
          .call()
          .then(res => {
            commit('setTokenTransfersCounter', res);
            resolve(res);
          })
          .catch(console.error);
      });
    });
  },
  /**
   * Get token transfer
   * @params id {number}
   * @res[0] "from" {string}
   * @res[1] "to" {string}
   * @res[2] "amount" {number}
   * @res[3] "value" {number}
   * @res[4] "createTime" {number}
   * @res[5] "resultTime" {number}
   * @res[6] "status" {number}
   * @res[7] "transferId" {number}
   * @res[8] "description" {string}
   */
  getTokenTransferDapp({ commit }, id) {
    contract().then(contract => {
      contract.methods.getTokenTransfer(id)
        .call()
        .then(res => {
          let data = {
            id,
            from: res[0],
            to: res[1],
            amount: parseInt(res[2]),
            value: parseInt(res[3]),
            createTime: parseInt(res[4]),
            resultTime: parseInt(res[5]),
            status: parseInt(res[6]),
            description: res[7]
          };
          commit('setTokenTransferEscrow', data);
        })
        .catch(console.error);
    });
  },
  /**
   * Company is used escrow
   * @res[0] {Boolean}
   */
  isUsedEscrowDapp({ commit }) {
    contract().then(contract => {
      contract.methods.isUsedEscrow()
        .call()
        .then(res => {
          commit('setIsUsedEscrow', res);
        })
        .catch(console.error);
    });
  },
  /**
   * Approve token transfer
   * @param id {number}
   * @param desc {string}
   */
  approveTokenTransferDapp({ commit, dispatch, getters }, { id, desc, props }) {
    commit('setEscrowTransferTokenPending', id);
    contract().then(contract => {
      contract.methods.approveTokenTransfer(id, desc)
        .send(from, (err, res) => {
          if (err) {
            commit('setEscrowTransferTokenPending', null);
            Vue.toasted.error(messages.transferNotApproved);
          } else {
            dispatch('sendAgentTransferMailExt', {
              from: props.from,
              to: props.to,
              amount: `${props.amount} ${getters.getTokenSymbol}`,
              time: Vue.moment().format(formatData.dateTime),
              tx: res
            });
          }
        });
    });
  },
  /**
   * Reject token transfer
   * @param id {number}
   * @param desc {string}
   */
  rejectTokenTransferDapp({ commit }, { id, desc }) {
    commit('setEscrowTransferTokenPending', id);
    contract().then(contract => {
      contract.methods.rejectTokenTransfer(id, desc)
        .send(from, (err) => {
          if (err) {
            commit('setEscrowTransferTokenPending', null);
            Vue.toasted.error(messages.transferNotRejected);
          }
        });
    });
  },
  /**
   * Approve trade transfer
   * @param id {number}
   * @param desc {string}
   * @return {Promise<void>}
   */
  approveTradeTransferDapp({ commit, dispatch, getters }, { id, desc, props }) {
    commit('setEscrowTradePending', id);
    contract().then(contract => {
      contract.methods.approveTradeTransfer(id, desc)
        .send(from, (err, res) => {
          if (err) {
            commit('setEscrowTradePending', null);
            Vue.toasted.error(messages.tradeNotApproved);
          } else {
            const order = getters.getOrders[props.orderId];
            dispatch('sendAgentTradeMailExt', {
              from: props.from,
              to: props.to,
              amount: `${props.amount} ${getters.getTokenSymbol}`,
              price: `${props.price} ETH`,
              time: Vue.moment().format(formatData.dateTime),
              mode: order.mode,
              tx: res
            });
          }
        });
    });
  },
  /**
   * Approve trade transfer
   * @param id {number}
   * @param desc {string}
   */
  rejectTradeTransferDapp({ commit }, { id, desc }) {
    commit('setEscrowTradePending', id);
    contract().then(contract => {
      contract.methods.rejectTradeTransfer(id, desc)
        .send(from, (err) => {
          if (err) {
            commit('setEscrowTradePending', null);
            Vue.toasted.error(messages.tradeNotRejected);
          }
        });
    });
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
